import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Phone,
  MapPin,
  Clock,
  ArrowUpRight,
  CheckCircle2,
  Activity,
  Droplets,
  Sparkles,
  Home as HomeIcon,
  Flame,
  Heart,
  HandHeart,
  Leaf,
  Star,
} from "lucide-react";

const DOCTOLIB_URL =
  "https://www.doctolib.de/physiotherapie/garbsen/giusi-borghese";

const GOOGLE_REVIEW_URL =
  "https://www.google.com/maps/search/?api=1&query=Physiotherapie+Borghese+Bruchstra%C3%9Fe+8+30952+Ronnenberg";

// Echte Patientenstimmen hier eintragen: { name: "Maria K.", text: "..." }
const testimonials = [];

const services = [
  {
    icon: Activity,
    title: "Krankengymnastik",
    desc: "Aktive und passive Übungen zur Wiederherstellung von Beweglichkeit, Kraft und Koordination.",
  },
  {
    icon: Droplets,
    title: "Manuelle Lymphdrainage",
    desc: "Sanfte Grifftechniken zur Anregung des Lymphflusses – bei Schwellungen und Stauungen.",
  },
  {
    icon: Sparkles,
    title: "CMD-Behandlung",
    desc: "Craniomandibuläre Dysfunktion: gezielte Therapie bei Kiefer-, Kopf- und Nackenbeschwerden.",
  },
  {
    icon: HomeIcon,
    title: "Hausbesuche",
    desc: "Physiotherapie in Ihrer gewohnten Umgebung – für alle, die nicht in die Praxis kommen können.",
  },
  {
    icon: Flame,
    title: "Wärmebehandlung / Fango",
    desc: "Wohltuende Wärmepackungen zur Muskelentspannung und Schmerzlinderung.",
  },
  {
    icon: Heart,
    title: "Beckenbodentraining",
    desc: "Individuelles Training zur Stärkung und bewussten Wahrnehmung des Beckenbodens.",
  },
  {
    icon: HandHeart,
    title: "Selbstzahlerleistungen",
    desc: "Zusätzliche Behandlungen, die über die Kassenleistungen hinausgehen – ganz nach Ihrem Bedarf.",
  },
  {
    icon: Leaf,
    title: "Körper & Seele",
    desc: "Achtsame Anwendungen, die Körper und Geist gleichermaßen ins Gleichgewicht bringen.",
  },
];

const reasons = [
  "Individuelle Betreuung – ich nehme mir Zeit für Ihre Beschwerden.",
  "Moderne, ruhige Praxisräume in Ronnenberg-Empelde.",
  "Ganzheitlicher Ansatz für Körper, Bewegung und Wohlbefinden.",
  "Termine flexibel – telefonisch oder bequem online buchbar.",
];

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/eb1qqarr_BAFC2AB2-C453-4A27-8927-AC045FD67DE5.png";
const INTERIOR_IMAGE =
  "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/acnl8i8t_0B949484-0C24-415C-AFFE-0DD89E43FF29.png";
const THERAPY_IMAGE = "https://images.pexels.com/photos/18120173/pexels-photo-18120173.jpeg";

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/bapft88q_51A8B921-D041-4A9C-8F4C-327CCBF7B0DA.png",
    title: "Babymassage & Babygymnastik",
    caption: "Bindung stärken, Entwicklung fördern, gemeinsam wachsen.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/k34geqb5_CE1D1AB7-546A-4C45-A53F-935FB3CFEFD0.png",
    title: "Wasserliege mit Farblichttherapie",
    caption: "Auszeit für Körper, Geist und Seele – wohltuende Entspannung.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/bxujsyfu_IMG_2776.jpeg",
    title: "Moderne Technologie",
    caption: "Body-RF-Technologie für Wärme, Entspannung und spürbare Wirkung.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_borghese-therapy/artifacts/5e6x4yyl_IMG_2792.jpeg",
    title: "Deine Gesundheit. Meine Leidenschaft.",
    caption: "Individuell, persönlich, ganzheitlich – für Ihr Wohlbefinden.",
  },
];

const Home = () => {
  return (
    <div data-testid="home-page" className="min-h-screen bg-[#FCF9F5]">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
      >
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#D8B4A0]/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#B56553]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#B56553] mb-8">
              <span className="w-8 h-px bg-[#B56553]" />
              Ronnenberg-Empelde
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#4A362E] tracking-tight">
              Physiotherapie
              <br />
              <em className="not-italic text-[#B56553] font-medium">Borghese</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#5C4E48] leading-relaxed max-w-xl">
              Ihre Physiotherapie in Ronnenberg-Empelde – persönlich, modern und
              ganzheitlich. Ich nehme mir Zeit für Ihre Beschwerden und erstelle
              eine Therapie, die zu Ihnen passt.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={DOCTOLIB_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-doctolib-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-[#B56553] hover:bg-[#9B5342] text-white font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_35px_rgba(181,101,83,0.28)]"
              >
                Online buchen über Doctolib
                <ArrowUpRight
                  size={18}
                  strokeWidth={1.75}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href="tel:+4917621684344"
                data-testid="hero-phone-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#4A362E]/20 text-[#4A362E] font-medium px-8 py-4 hover:bg-[#4A362E] hover:text-white transition-all duration-300"
              >
                <Phone size={17} strokeWidth={1.75} />
                0176 21684344
              </a>
            </div>

            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#5C4E48] fade-up-delay-2">
              <div className="flex items-center gap-2">
                <MapPin size={15} className="text-[#B56553]" strokeWidth={1.5} />
                Bruchstraße 8, 30952 Ronnenberg
              </div>
              <div className="flex items-center gap-2">
                <Clock size={15} className="text-[#B56553]" strokeWidth={1.5} />
                Mo. ab 07:30 Uhr · Weitere Zeiten n. V.
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up-delay-1">
            <div className="relative aspect-square overflow-hidden rounded-3xl bg-[#F7F2EB] shadow-[0_25px_60px_rgba(74,54,46,0.15)]">
              <img
                src={HERO_IMAGE}
                alt="Physiotherapie Borghese – Körper, Gesundheit, Seele im Einklang"
                className="w-full h-full object-contain"
                loading="eager"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 bg-white rounded-2xl px-6 py-5 shadow-[0_15px_40px_rgba(74,54,46,0.08)] border border-[#E8DFD8] max-w-[240px]">
              <p className="font-serif text-2xl text-[#4A362E] leading-tight">
                Zeit für Sie.
              </p>
              <p className="text-sm text-[#5C4E48] mt-1">
                Persönlich. Ganzheitlich.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Willkommen */}
      <section
        id="willkommen"
        data-testid="willkommen-section"
        className="py-24 md:py-32 px-6 md:px-10 bg-[#F7F2EB]"
      >
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
            Willkommen
          </span>
          <p className="mt-10 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.25] text-[#4A362E]">
            „Ich nehme mir Zeit für Ihre Beschwerden und erstelle eine Therapie,
            die zu <em className="text-[#B56553]">Ihnen</em> passt.&rdquo;
          </p>
          <p className="mt-10 text-lg text-[#5C4E48] leading-relaxed max-w-2xl mx-auto">
            In meiner Praxis erwartet Sie eine persönliche, moderne und
            ganzheitliche physiotherapeutische Behandlung – in einer ruhigen
            Atmosphäre, in der Sie sich gut aufgehoben fühlen.
          </p>
        </div>
      </section>

      {/* Leistungen */}
      <section
        id="leistungen"
        data-testid="leistungen-section"
        className="py-24 md:py-32 px-6 md:px-10"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-xl">
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
                Leistungen
              </span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A362E] leading-tight">
                Behandlungen mit Sorgfalt
              </h2>
            </div>
            <p className="text-[#5C4E48] max-w-md leading-relaxed">
              Von klassischer Krankengymnastik bis zu ganzheitlichen Angeboten
              für Körper und Seele – ein Überblick über mein therapeutisches
              Angebot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="group relative bg-white border border-[#E8DFD8] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(74,54,46,0.08)]"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F7F2EB] flex items-center justify-center mb-6 group-hover:bg-[#B56553] transition-colors duration-300">
                    <Icon
                      size={22}
                      strokeWidth={1.5}
                      className="text-[#B56553] group-hover:text-white transition-colors duration-300"
                    />
                  </div>
                  <h3 className="font-serif text-xl text-[#4A362E]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#5C4E48] leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Warum Borghese */}
      <section
        id="warum"
        data-testid="warum-section"
        className="py-24 md:py-32 px-6 md:px-10 bg-[#F7F2EB]"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <div className="relative">
            <div className="aspect-square overflow-hidden rounded-3xl bg-[#FCF9F5] border border-[#E8DFD8] shadow-[0_25px_60px_rgba(74,54,46,0.15)]">
              <img
                src={INTERIOR_IMAGE}
                alt="Deine Gesundheit. Meine Leidenschaft."
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 bg-[#B56553] text-white rounded-2xl px-7 py-6 shadow-[0_15px_40px_rgba(181,101,83,0.35)] max-w-[260px]">
              <p className="font-serif text-2xl leading-tight">
                Eine ruhige Atmosphäre.
              </p>
              <p className="text-sm mt-2 opacity-90">
                Damit Sie sich gut aufgehoben fühlen.
              </p>
            </div>
          </div>

          <div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
              Warum Borghese?
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#4A362E] leading-tight">
              Individuelle Betreuung, spürbar anders.
            </h2>
            <p className="mt-6 text-lg text-[#5C4E48] leading-relaxed">
              Moderne Praxisräume, eine ruhige Atmosphäre und eine Therapie,
              die auf Sie zugeschnitten ist – das ist mein Anspruch.
            </p>
            <ul className="mt-10 space-y-5">
              {reasons.map((r, i) => (
                <li
                  key={r}
                  data-testid={`reason-item-${i}`}
                  className="flex items-start gap-4"
                >
                  <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-white border border-[#E8DFD8] flex items-center justify-center">
                    <CheckCircle2
                      size={16}
                      strokeWidth={1.75}
                      className="text-[#B56553]"
                    />
                  </span>
                  <span className="text-[#4A362E] text-base leading-relaxed pt-0.5">
                    {r}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Einblicke / Galerie */}
      <section
        id="einblicke"
        data-testid="einblicke-section"
        className="py-24 md:py-32 px-6 md:px-10"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-xl">
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
                Einblicke
              </span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A362E] leading-tight">
                Ein Blick in
                <br />
                meine Welt.
              </h2>
            </div>
            <p className="text-[#5C4E48] max-w-md leading-relaxed">
              Impressionen und Angebote aus meiner Praxis – von Babymassage bis
              hin zu modernen Anwendungen wie der Wasserliege mit
              Farblichttherapie.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {galleryImages.map((g, i) => (
              <figure
                key={g.title}
                data-testid={`einblick-card-${i}`}
                className="group relative overflow-hidden rounded-3xl bg-[#F7F2EB] border border-[#E8DFD8] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(74,54,46,0.10)]"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={g.src}
                    alt={g.title}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
                <figcaption className="px-7 py-6 border-t border-[#E8DFD8] bg-white">
                  <p className="font-serif text-xl text-[#4A362E] leading-tight">
                    {g.title}
                  </p>
                  <p className="mt-2 text-sm text-[#5C4E48] leading-relaxed">
                    {g.caption}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Bewertungen */}
      <section
        id="bewertungen"
        data-testid="bewertungen-section"
        className="py-24 md:py-32 px-6 md:px-10 bg-[#F7F2EB]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="max-w-xl mb-16">
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
              Bewertungen
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A362E] leading-tight">
              Ihre Meinung
              <br />
              zählt.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                data-testid={`testimonial-card-${i}`}
                className="bg-white border border-[#E8DFD8] rounded-3xl p-8 md:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(74,54,46,0.10)]"
              >
                <div className="flex gap-1 text-[#B56553]">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="mt-5 text-[#5C4E48] leading-relaxed flex-1">
                  „{t.text}&ldquo;
                </blockquote>
                <figcaption className="mt-6 font-serif text-lg text-[#4A362E]">
                  {t.name}
                </figcaption>
              </figure>
            ))}

            <div
              data-testid="google-review-card"
              className={`relative overflow-hidden bg-[#B56553] text-white rounded-3xl p-8 md:p-10 flex flex-col justify-between ${
                testimonials.length === 0
                  ? "md:col-span-2 lg:col-span-3 md:flex-row md:items-center gap-8"
                  : ""
              }`}
            >
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
              <div className="max-w-lg">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={18} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="mt-5 font-serif text-2xl md:text-3xl leading-snug">
                  Waren Sie bei mir in Behandlung?
                </p>
                <p className="mt-3 text-white/85 leading-relaxed">
                  Ihre Erfahrung hilft anderen Patientinnen und Patienten, den
                  Weg in meine Praxis zu finden. Ich freue mich über Ihre
                  Bewertung auf Google.
                </p>
              </div>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-btn"
                className="mt-8 md:mt-0 inline-flex items-center justify-center gap-2 bg-white text-[#B56553] px-7 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 hover:bg-[#FCF9F5] hover:shadow-lg shrink-0 self-start md:self-center"
              >
                Auf Google bewerten
                <ArrowUpRight size={16} strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Termine & Öffnungszeiten */}
      <section
        id="termine"
        data-testid="termine-section"
        className="py-24 md:py-32 px-6 md:px-10"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Terminbuchung – Terrakotta Highlight */}
          <div
            data-testid="booking-box"
            className="relative overflow-hidden bg-[#B56553] text-white rounded-3xl p-10 md:p-14"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-2xl pointer-events-none" />
            <span className="text-[11px] tracking-[0.28em] uppercase opacity-80">
              Termine
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              Jetzt Termin
              <br />
              vereinbaren
            </h2>
            <p className="mt-5 text-white/85 leading-relaxed max-w-md">
              Buchen Sie bequem online über Doctolib oder rufen Sie mich direkt
              an – ich freue mich auf Sie.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={DOCTOLIB_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="termine-doctolib-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#4A362E] font-medium px-7 py-3.5 hover:bg-[#FCF9F5] transition-colors"
              >
                Online buchen
                <ArrowUpRight size={17} strokeWidth={1.75} />
              </a>
              <a
                href="tel:+4917621684344"
                data-testid="termine-phone-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/50 text-white font-medium px-7 py-3.5 hover:bg-white/10 transition-colors"
              >
                <Phone size={16} strokeWidth={1.75} />
                0176 21684344
              </a>
            </div>
          </div>

          {/* Öffnungszeiten */}
          <div
            data-testid="hours-box"
            className="relative bg-[#F7F2EB] border border-[#E8DFD8] rounded-3xl p-10 md:p-14 overflow-hidden"
          >
            <img
              src={THERAPY_IMAGE}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none"
              loading="lazy"
            />
            <div className="relative">
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
                Öffnungszeiten
              </span>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl text-[#4A362E] leading-tight">
                Wann Sie mich erreichen.
              </h3>
              <ul className="mt-10 space-y-4 text-[#4A362E]">
                <li className="flex items-center justify-between border-b border-[#E8DFD8] pb-4">
                  <span className="font-medium">Montag & Mittwoch</span>
                  <span className="text-[#5C4E48]">Hausbesuche</span>
                </li>
                <li className="flex items-center justify-between border-b border-[#E8DFD8] pb-4">
                  <span className="font-medium">Dienstag, Donnerstag & Freitag</span>
                  <span className="text-[#5C4E48]">ab 07:00 Uhr, nach Terminvergabe</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="font-medium">Samstag & Sonntag</span>
                  <span className="text-[#5C4E48]">geschlossen</span>
                </li>
              </ul>
              <p className="mt-8 text-sm text-[#5C4E48] leading-relaxed">
                Weitere Termine gerne nach individueller Absprache – rufen Sie
                mich einfach an.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section
        id="kontakt"
        data-testid="kontakt-section"
        className="py-24 md:py-32 px-6 md:px-10 bg-[#F7F2EB]"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#B56553]">
              Kontakt
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A362E] leading-tight">
              So finden Sie
              <br />
              zu mir.
            </h2>
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8DFD8] flex items-center justify-center flex-shrink-0">
                  <MapPin
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#B56553]"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#B56553] mb-1">
                    Adresse
                  </p>
                  <p className="text-lg text-[#4A362E]">
                    Physiotherapie Borghese
                    <br />
                    Bruchstraße 8
                    <br />
                    30952 Ronnenberg-Empelde
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8DFD8] flex items-center justify-center flex-shrink-0">
                  <Phone
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#B56553]"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#B56553] mb-1">
                    Telefon
                  </p>
                  <a
                    href="tel:+4917621684344"
                    data-testid="kontakt-phone-link"
                    className="text-lg text-[#4A362E] hover:text-[#B56553] transition-colors"
                  >
                    0176 21684344
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8DFD8] flex items-center justify-center flex-shrink-0">
                  <Clock
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#B56553]"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#B56553] mb-1">
                    Sprechzeiten
                  </p>
                  <p className="text-lg text-[#4A362E]">
                    Mo. ab 07:30 Uhr
                    <br />
                    Weitere Zeiten nach Vereinbarung
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            data-testid="map-embed"
            className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#E8DFD8] shadow-[0_20px_50px_rgba(74,54,46,0.1)]"
          >
            <iframe
              title="Praxisstandort Physiotherapie Borghese"
              src="https://www.google.com/maps?q=Bruchstra%C3%9Fe+8,+30952+Ronnenberg&output=embed"
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
